import React from "react";
import {
  CategoryOption,
  CategoryOptionConfig,
  OTHER_OPTION_LABEL,
} from "../utils/categoryOptions";
import { Button } from "./Button";

type Props = {
  categoryContent?: CategoryOptionConfig;
  selectedCategoryOption: string;
  otherCategoryText: string;
  goalError?: string;
  submitting?: boolean;
  onBack: () => void;
  onCategorySelect: (option: CategoryOption) => void;
  onOtherInput: (value: string) => void;
  onContinue: () => void;
};

export const CategorySelectionStep: React.FC<Props> = ({
  categoryContent,
  selectedCategoryOption,
  otherCategoryText,
  goalError,
  submitting = false,
  onBack,
  onCategorySelect,
  onOtherInput,
  onContinue,
}) => {
  if (!categoryContent) {
    return null;
  }
  const isOtherSelected = selectedCategoryOption === OTHER_OPTION_LABEL;
  const isContinueDisabled =
    !selectedCategoryOption ||
    (isOtherSelected && !otherCategoryText.trim()) ||
    submitting;

  return (
    <div className="w-full">
      <div className="container-white-rounded max-w-3xl bg-white shadow-md">
        <Button
          type="button"
          onClick={onBack}
          className="mb-6 flex items-center text-sm font-semibold text-gray-600 transition hover:text-black"
          text="Back to options"
          contentClassName="flex items-center gap-2"
          variant="unstyled"
        >
          <span aria-hidden="true" className="mr-2">
            &larr;
          </span>
          <span>Back to options</span>
        </Button>

        <h2 className="text-3xl font-black text-gray-900">
          {categoryContent.heading}
        </h2>
        <div className="mt-6 grid gap-3 sm:grid-cols-2">
          {categoryContent.options.map((option) => {
            const isSelected = option.label === selectedCategoryOption;
            return (
              <Button
                key={option.label}
                type="button"
                onClick={() => onCategorySelect(option)}
                className={`flex items-center gap-3 rounded-2xl border px-4 py-3 text-left font-semibold transition focus:outline-none focus-visible:ring-2 focus-visible:ring-black ${
                  isSelected
                    ? "border-black bg-white shadow-md"
                    : "border-black/10 bg-white hover:border-black/40"
                }`}
                variant="unstyled"
                contentClassName="flex items-center gap-3"
                text={option.label}
              >
                <span
                  className={`flex h-5 w-5 items-center justify-center rounded-full border ${
                    isSelected ? "border-black" : "border-gray-400"
                  }`}
                >
                  {isSelected && (
                    <span className="h-2.5 w-2.5 rounded-full bg-black" />
                  )}
                </span>
                {option.label}
              </Button>
            );
          })}
        </div>

        {isOtherSelected && (
          <div className="mt-6">
            <label
              htmlFor="category-other-input"
              className="text-sm font-semibold text-gray-800"
            >
              Tell us what needs fixing
            </label>
            <input
              id="category-other-input"
              type="text"
              value={otherCategoryText}
              onChange={(e) => onOtherInput(e.target.value)}
              className="mt-2 w-full rounded-2xl border border-black/10 p-3 text-base text-gray-900 focus:border-black focus:outline-none"
              placeholder="Eg: Shower, sink, dryer"
              maxLength={120}
            />
            {goalError && <p className="mt-2 text-sm text-red">{goalError}</p>}
          </div>
        )}

        <div className="mt-8">
          <Button
            text="Continue"
            type="button"
            onClick={onContinue}
            className="w-full"
            disabled={isContinueDisabled}
            isLoading={submitting}
            loadingText="Saving..."
          />
        </div>
      </div>
    </div>
  );
};
